import { useCallback, useEffect, useState } from "react";

import { AlertTriangle, RotateCw } from "lucide-react";

import { ApiProblem, type InboxFuryWebPort } from "~/api/ports";
import type { SessionView } from "~/domain/types";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { FOCUS_RING } from "~/lib/utils";

/**
 * Whether the person holding this browser has a session.
 *
 * WHY THIS IS FOUR STATES AND NOT A BOOLEAN. `getSession()` **rejects** when
 * there is no session — an `ApiProblem` with status 401 — rather than resolving
 * to null, so "signed out" and "the BFF is unreachable" arrive down the same
 * channel and have to be told apart here. They are different situations with
 * opposite right answers: the first is somebody who needs to sign in, the
 * second is somebody who is *already* signed in and must not be thrown out of
 * the product because one request failed. Collapsing them would trade a defect
 * that shows strangers an empty page for a defect that signs everybody out
 * during a deploy, which is worse.
 *
 * And `resolving` is a state in its own right, not a falsy `authenticated`.
 * Treating "not yet known" as "signed in" is the defect this module exists to
 * fix; treating it as "signed out" would bounce every legitimate visitor
 * through the sign-in page on every cold load.
 */
export type SessionResolution =
  | { status: "resolving" }
  | { status: "authenticated"; session: SessionView }
  | { status: "anonymous" }
  | { status: "unavailable"; detail: string };

export interface SessionGate {
  resolution: SessionResolution;
  /** Ask the server again. Only reachable from the `unavailable` notice. */
  retry: () => void;
}

/**
 * Resolve the session once per client, and not once per navigation.
 *
 * The effect depends on the client instance alone, so it runs on mount and then
 * never again while the document lives — deliberately. The client is built once
 * in `main.tsx` and handed down, so this is one `GET /session` per page load,
 * the same shape as the runtime's readiness probe. Both of the places that
 * change the answer — signing in and signing out — do a full document load
 * precisely because the cookie changes underneath every open fetch, so there is
 * no in-page transition this would need to notice.
 *
 * `getSession` is a GET, so it takes the non-mutating path through the client
 * and never touches `/session/csrf`. Booting the app costs one request.
 */
export function useSessionResolution(client: InboxFuryWebPort): SessionGate {
  const [attempt, setAttempt] = useState(0);
  const [resolution, setResolution] = useState<SessionResolution>({ status: "resolving" });

  useEffect(() => {
    // StrictMode mounts twice in development and this runs twice with it. That
    // is harmless: the request is a GET with no side effect, and the latch below
    // means only the live one is allowed to speak.
    let live = true;
    void client.getSession().then(
      (session) => {
        if (live) setResolution({ status: "authenticated", session });
      },
      (error: unknown) => {
        if (!live) return;
        // Only 401 is "no session". A 403 is a session the server will not
        // honour for this request — that is not an answer of "signed out", and
        // treating it as one would bounce a still-signed-in operator to
        // /signin. Same for every other status: if we could not ask, we must
        // not invent "no".
        if (error instanceof ApiProblem && error.status === 401) {
          setResolution({ status: "anonymous" });
          return;
        }
        // Everything else — a 5xx, a 403, a network failure, a
        // `redirect: "error"` TypeError from an interception — is a question
        // we could not ask, not an answer of "no".
        setResolution({
          status: "unavailable",
          detail:
            error instanceof ApiProblem
              ? error.detail
              : "InboxFury could not reach the service that confirms who you are.",
        });
      },
    );
    return () => {
      live = false;
    };
  }, [client, attempt]);

  const retry = useCallback(() => {
    setResolution({ status: "resolving" });
    setAttempt((previous) => previous + 1);
  }, []);

  return { resolution, retry };
}

/**
 * What stands in for the product while the session is not yet established.
 *
 * The hard rule this component exists to keep: **resolving and anonymous must
 * not look like the signed-in product.** No sidebar, no page header, no table,
 * and above all no empty state — telling somebody who is not signed in that
 * they have no API keys is a claim about an account, and it is a false one. It
 * also stops being merely misleading the moment a container renders before the
 * session resolves, at which point the same page is a real disclosure.
 *
 * Unavailable is the exception: the operator may already be signed in, so the
 * notice is the product `ErrorState` card (shadowed `bg-card`, ember Retry)
 * and nothing else — still no sidebar, and no sign-in link that would treat an
 * outage as signed-out.
 *
 * It does render one `<main>`, because a document with no main landmark is a
 * document a screen-reader user cannot skip into, and the gate can be on screen
 * for a whole round trip.
 */
export function SessionGateNotice({
  resolution,
  onRetry,
}: {
  resolution: SessionResolution;
  onRetry: () => void;
}) {
  const busy = resolution.status === "resolving" || resolution.status === "anonymous";
  const detail = resolution.status === "unavailable" ? resolution.detail : undefined;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-8">
      <main id="main-content" role="main" className="w-full max-w-md">
        {busy ? (
          <p
            aria-busy="true"
            aria-live="polite"
            className="text-center text-sm text-foreground-muted"
          >
            {resolution.status === "resolving"
              ? "Checking your session…"
              : "Taking you to sign in…"}
          </p>
        ) : (
          <Card
            data-slot="error-state"
            role="alert"
            className="w-full min-w-0 gap-0 rounded-lg bg-card py-0 shadow-sm"
          >
            <div className="flex items-start gap-3 px-4 py-3">
              <AlertTriangle
                aria-hidden="true"
                className="mt-0.5 size-4 shrink-0 text-error-ink"
              />
              <div className="min-w-0">
                <h1 className="text-[13px] font-semibold leading-snug tracking-tight text-foreground">
                  We could not confirm your session
                </h1>
                {detail ? (
                  <p className="mt-0.5 max-w-prose text-[13px] leading-snug break-words text-foreground-muted [overflow-wrap:anywhere]">
                    {detail}
                  </p>
                ) : null}
              </div>
            </div>
            {/*
              Retry only. Whoever is here may well be signed in already and
              looking at a service having a bad minute. A sign-in link would
              treat that outage as signed-out.
            */}
            <div className="flex flex-wrap items-center gap-2 border-t border-border px-4 py-2.5">
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={onRetry}
                className={FOCUS_RING}
              >
                <RotateCw aria-hidden="true" className="size-3.5 shrink-0" />
                Retry
              </Button>
            </div>
          </Card>
        )}
      </main>
    </div>
  );
}
